import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, tap, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { catchError } from 'rxjs/operators';

/** Znaki sterujące — ich obecność znaczy, że „odkodowany” tekst to w rzeczywistości śmieci. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[ --]/;

@Injectable({
  providedIn: 'root',
})
export class ServerMessageInterceptor implements HttpInterceptor {
  private inj = inject(Injector);

// Resolved lazily (not in the constructor) to avoid a circular dependency through HttpClient.
  private get toast(): ToastrService {
    return this.inj.get(ToastrService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.checkServerResponse(error);
        return throwError(() => error);
      }),
      tap((response) => {
        if (response instanceof HttpResponse) {
          this.checkServerResponse(response);
        }
      })
    );
  }

  private checkServerResponse(response: HttpResponse<any> | HttpErrorResponse): void {
    let message = response?.headers?.get('server-response-message');
    const level = response?.headers?.get('server-response-level')?.toLowerCase();

    message = this.decodeIfBase64(message);

    if (message) {
      switch (level) {
        case 'error':
          this.toast.error(`${message}`);
          break;
        case 'warning':
          this.toast.warning(`${message}`);
          break;
        default:
          this.toast.info(`${message}`);
          break;
      }
    }
  }

  /**
   * Backend wysyła komunikat albo wprost, albo w base64 (polskie znaki w nagłówku HTTP).
   *
   * Sam test `btoa(atob(x)) === x` nie wystarcza: zwykłe słowa też bywają poprawnym base64
   * — „Zapisano” ma osiem liter z alfabetu base64, więc dawny kod zamieniał ten komunikat
   * w krzaki. Dekodujemy więc tylko wtedy, gdy wynik jest poprawnym UTF-8 bez znaków sterujących.
   */
  private decodeIfBase64(message: string | null): string | null {
    if (!message) {
      return message;
    }

    let bytes: Uint8Array;
    try {
      if (btoa(atob(message)) !== message) {
        return message;
      }
      bytes = Uint8Array.from(atob(message), (c) => c.charCodeAt(0));
    } catch {
      return message;
    }

    try {
      const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      return CONTROL_CHARACTERS.test(decoded) ? message : decoded;
    } catch {
      return message;
    }
  }
}
